import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import clearSearch from "@insite/client-framework/Store/Pages/SavedOrderList/Handlers/ClearSearch";
import updateSearchFields from "@insite/client-framework/Store/Pages/SavedOrderList/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonProps } from "@insite/mobius/Button";
import { DatePickerState } from "@insite/mobius/DatePicker";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Tag from "@insite/mobius/Tag";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TagOutline, { TagPresentationProps } from "@ucs/Components/TagOutline";
import CloseRounded from "@ucs/Icons/CloseRounded";
import UCSColors from "@ucs/Utils/UCSColors";
import * as React from "react";
import { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    getCartsApiParameter: state.pages.savedOrderList.getCartsApiParameter,
});

const mapDispatchToProps = {
    updateSearchFields,
    clearSearch,
};

interface OwnProps {
    verticalLayout?: boolean;
    extendedStyles?: SavedOrderListFilterPillsStyles;
    setOrderSubtotal?: React.Dispatch<React.SetStateAction<string>>
    setOrderSubtotalOperator?: React.Dispatch<React.SetStateAction<string>>
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SavedOrderListFilterPillsStyles {
    filterTag?: TagPresentationProps;
    clearLink?: LinkPresentationProps;
    appliedFiltersContainer?: InjectableCss;
    clearFiltersButton?: ButtonProps;
}

export const savedOrderListFilterPillsStyles: SavedOrderListFilterPillsStyles = {
    filterTag: {
        typographyProps: {
            css: css`
                text-transform: uppercase;
                padding: 8px 5px 8px 0px;
            `,
        },
    },
    clearLink: {
        icon: {
            iconProps: {
                src: CloseRounded,
                size: 20,
                color: "primary",
                css: css`
                    margin-right: 8px;
                `,
            },
        },
        typographyProps: {
            size: 18,
            css: css`
                color: ${getColor("primary")} !important;
            `,
        },
        css: css`
            margin-left: 6px;
            margin-top: 2px;
        `,
    },
    appliedFiltersContainer: {
        css: css`
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            align-items: flex-start;
            gap: 8px;
        `
    },
    clearFiltersButton: {
        variant: "secondary",
        css: css`
        margin-left : auto;
        margin-bottom: 15px;
        `
    },
}

const FilterPills = ({
    extendedStyles,
    verticalLayout,
    getCartsApiParameter,
    updateSearchFields,
    clearSearch,
    setOrderSubtotal,
    setOrderSubtotalOperator
}: Props) => {
    const [styles] = useState(() => mergeToNew(savedOrderListFilterPillsStyles, extendedStyles));
    const tzOffset = new Date().getTimezoneOffset() * 60000;

    const fromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        updateSearchFields({
            fromDate: selectedDay ? new Date(selectedDay.getTime() - tzOffset).toISOString().split("T")[0] : "",
        });
    };

    const toDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        updateSearchFields({
            toDate: selectedDay ? new Date(selectedDay.getTime() - tzOffset).toISOString().split("T")[0] : "",
        });
    };

    const resetOrderSubtotal = () => {
        setOrderSubtotal ? setOrderSubtotal("") : null;
        setOrderSubtotalOperator ? setOrderSubtotalOperator("") : null;
        updateSearchFields({ orderSubtotalOperator: "", orderSubtotal: "" });
    };

    const clearFiltersClickHandler = () => {
        setOrderSubtotal ? setOrderSubtotal("") : null;
        setOrderSubtotalOperator ? setOrderSubtotalOperator("") : null;
        clearSearch();
    };

    return (
        <StyledWrapper {...styles.appliedFiltersContainer}>

            {getCartsApiParameter.fromDate && (
                <Tag
                    {...styles.filterTag}
                    onDelete={() => {
                        fromDateChangeHandler({});
                    }}
                >
                    {`${translate("From")}: ${getCartsApiParameter.fromDate}`}
                </Tag>
            )}
            {getCartsApiParameter.toDate && (
                <Tag
                    {...styles.filterTag}
                    onDelete={() => {
                        toDateChangeHandler({});
                    }}
                >
                    {`${translate("To")}: ${getCartsApiParameter.toDate}`}
                </Tag>
            )}
            {getCartsApiParameter.orderSubtotalOperator && getCartsApiParameter.orderSubtotal && (
                <Tag
                    {...styles.filterTag}
                    onDelete={() => {
                        resetOrderSubtotal();
                    }}
                >
                    {`${translate("Order Subtotal")}: ${getCartsApiParameter.orderSubtotalOperator} ${getCartsApiParameter.orderSubtotal
                        }`}
                </Tag>
            )}
            {(getCartsApiParameter.fromDate || getCartsApiParameter.toDate ||
                (getCartsApiParameter.orderSubtotalOperator && getCartsApiParameter.orderSubtotal)) && (
                    <Button
                        {...styles.clearFiltersButton}
                        onClick={clearFiltersClickHandler}
                        data-test-selector="rfqMyQuotes_clearFiltersButton"
                    >
                        {translate("Clear Filters")}
                    </Button>
                )
            }
        </StyledWrapper>
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(FilterPills);